import {
    FinancialDashboardStats,
    FinancialTypeFilter,
} from '@/lib/api/financial/financial-dashboard-api'
import {
    MdArrowDownward,
    MdArrowUpward,
    MdAttachMoney,
    MdInventory2,
    MdReceiptLong,
    MdShowChart,
    MdWarning,
} from 'react-icons/md'
import { FinancialHelpTopic } from './financial-help-modal'
import FinancialStatCard from './financial-stat-card'

const compact = (cents: number) => {
    const value = cents / 100
    if (Math.abs(value) >= 1_000_000)
        return `R$ ${(value / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`
    if (Math.abs(value) >= 10_000)
        return `R$ ${(value / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

interface FinancialSummaryCardsProps {
    stats: FinancialDashboardStats
    /**
     * Status da cobrança em vigor no filtro. Fechado em "Pago", "A Receber" e "A Pagar"
     * somam dinheiro que JÁ andou — e os dois rótulos se leem como pendência.
     */
    billingStatus?: string
    /** Lado do caixa no seletor: decide qual vencido o card mostra. */
    type?: FinancialTypeFilter
    onHelp?: (topic: FinancialHelpTopic) => void
}

/**
 * Os números do período, na ordem em que se lê o resultado.
 *
 * **Custo e despesa não são a mesma coisa**, e o card do meio já foi um só chamado
 * "Despesa" que somava conta a pagar QUITADA no período. Repasse só é quitado quando
 * a transferência é aprovada, e a fila anda em outro ritmo: em produção, agosto/2026
 * mostrou R$ 264,3 mil de receita, R$ 250,00 de "despesa" e lucro igual à receita.
 *
 * Agora:
 * - **Custo** — o que a receita do período consumiu: repasse congelado na venda
 *   (parceiro, profissional, hospital) mais insumos. Vem do lado da RECEITA, não da
 *   fila de pagamento.
 * - **Despesa** — o gasto da operação que não pertence a venda nenhuma: aluguel,
 *   energia, material, folha. Repasse não entra aqui, senão o mesmo dinheiro contaria
 *   duas vezes.
 * - **Lucro** — receita − custo − despesa.
 */
export default function FinancialSummaryCards({
    stats,
    billingStatus,
    type = 'ALL',
    onHelp,
}: FinancialSummaryCardsProps) {
    /**
     * Com o recorte em "Pago", estes dois cards mostram o que entrou e o que saiu, não
     * o que está por vir.
     *
     * Não viram "Receita"/"Despesa": esses nomes já são de outros dois cards desta
     * mesma linha, com outra conta por trás (receita realizada, despesa operacional).
     * Dois cards com o mesmo nome e valores diferentes seria pior que o rótulo errado.
     */
    const realizado = billingStatus === 'PAID'
    // Cobrança recebida sem repasse apurado entra na receita com custo zero e infla o
    // lucro. Sem este aviso, o número parece cobrir tudo.
    const costSubtitle =
        stats.costUnknownCount > 0
            ? `${stats.costUnknownCount} sem custo apurado`
            : 'Do que foi recebido'

    // "Vencidos" segue o lado escolhido; em "Todos" mostra o a receber e cita o a pagar
    // embaixo — somar os dois num número só misturava dívida de cliente com repasse.
    const showPayableOverdue = type === 'PAYABLE'
    const overdueValue = showPayableOverdue ? stats.overduePayableAmount : stats.overdueReceivableAmount
    const overdueCount = showPayableOverdue ? stats.overduePayableCount : stats.overdueReceivableCount
    const overdueSubtitle =
        type === 'ALL' && stats.overduePayableAmount > 0
            ? `${overdueCount} lançamento(s) · a pagar: ${compact(stats.overduePayableAmount)}`
            : `${overdueCount} lançamento(s), de qualquer data`
    const help = (topic: FinancialHelpTopic) => (onHelp ? () => onHelp(topic) : undefined)

    return (
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            <FinancialStatCard
                title={realizado ? 'Recebido' : 'A Receber'}
                value={realizado ? stats.totalReceivable : stats.openReceivable}
                subtitle={
                    realizado
                        ? 'Vencimento no período'
                        : `Em aberto · de ${compact(stats.totalReceivable)} no período`
                }
                icon={MdArrowDownward}
                color="success"
                onHelp={help('receivable')}
            />
            <FinancialStatCard
                title={realizado ? 'Pago' : 'A Pagar'}
                value={realizado ? stats.totalPayable : stats.openPayable}
                subtitle={
                    realizado
                        ? 'Vencimento no período'
                        : `Em aberto · de ${compact(stats.totalPayable)} no período`
                }
                icon={MdArrowUpward}
                color="error"
                onHelp={help('payable')}
            />
            <FinancialStatCard
                title="Receita"
                value={stats.revenue}
                subtitle="Entrou no período"
                icon={MdAttachMoney}
                color="success"
                onHelp={help('revenue')}
            />
            <FinancialStatCard
                title="Custo"
                value={stats.cost}
                subtitle={costSubtitle}
                icon={MdInventory2}
                color="warning"
                onHelp={help('cost')}
            />
            <FinancialStatCard
                title="Despesa"
                value={stats.expense}
                subtitle="Operacional paga"
                icon={MdReceiptLong}
                color="error"
                onHelp={help('expense')}
            />
            <FinancialStatCard
                title="Lucro"
                value={stats.profit}
                subtitle="Receita - Custo - Despesa"
                icon={MdShowChart}
                color={stats.profit >= 0 ? 'primary' : 'error'}
                onHelp={help('profit')}
            />
            <FinancialStatCard
                title={showPayableOverdue ? 'Vencidos a pagar' : 'Vencidos a receber'}
                value={overdueValue}
                subtitle={overdueSubtitle}
                icon={MdWarning}
                color="error"
                onHelp={help('overdue')}
            />
        </div>
    )
}
